import { Alert, Column } from "@hightouch/htui";
import { EmbedNotices } from "src/pages/ad-studio/insights/embed-notices";
import {
  useAdCardGridEmbed,
  useCardGridEmbedPlan,
} from "src/pages/ad-studio/insights/use-ad-card-grid-embed";
import { FailureAlert } from "src/pages/ad-studio/insights/performance/failure-alert";
import type {
  CardGridPlan,
  PilledAdRow,
} from "src/pages/ad-studio/insights/performance/types";
import type { EmbedAttrs } from "src/pages/ad-studio/insights/performance/utils/embeds";
import { CardsSkeleton } from "src/pages/ad-studio/insights/performance/view/cards-skeleton";
import { CardsEmpty } from "src/pages/ad-studio/insights/performance/view/cards-empty";
import { CardsGrid } from "src/pages/ad-studio/insights/performance/view/cards-grid";
import { PerformanceCard } from "src/pages/ad-studio/insights/performance/view/performance-card";
import type {
  CardItem,
  EmptyText,
  PagingState,
} from "src/pages/ad-studio/insights/performance/view/types";

type AdCardGridEmbedProps = { attrs: EmbedAttrs };

const ShownCards = ({
  cards,
  paging,
  empty,
}: {
  cards: readonly CardItem<PilledAdRow>[];
  paging: PagingState;
  empty: EmptyText;
}) => {
  switch (paging.kind) {
    case "no-rows": {
      return <CardsEmpty empty={empty} />;
    }
    case "one-page":
    case "pages": {
      return (
        <CardsGrid>
          {cards.map(({ row, card }) => (
            <PerformanceCard key={row.entityId} card={card} />
          ))}
        </CardsGrid>
      );
    }
    default: {
      return paging satisfies never;
    }
  }
};

const PlannedAdCardGrid = ({ plan }: { plan: CardGridPlan }) => {
  const { state } = useAdCardGridEmbed({ plan });
  switch (state.grid.status) {
    case "loading": {
      return <CardsSkeleton />;
    }
    case "failed": {
      return <FailureAlert failure={state.grid.failure} />;
    }
    case "loaded": {
      return (
        <ShownCards
          cards={state.grid.value.cards}
          paging={state.grid.value.paging}
          empty={state.grid.value.empty}
        />
      );
    }
    default: {
      return state.grid satisfies never;
    }
  }
};

export const AdCardGridEmbed = ({ attrs }: AdCardGridEmbedProps) => {
  const { state } = useCardGridEmbedPlan(attrs);
  switch (state.status) {
    case "loading": {
      return <CardsSkeleton />;
    }
    case "failed": {
      return <FailureAlert failure={state.failure} />;
    }
    case "no-connected-channels": {
      return (
        <Alert
          type="info"
          title="No ad platforms are connected"
          message="Connect one to see its ads here."
        />
      );
    }
    case "planned": {
      return (
        <Column gap={2}>
          <EmbedNotices notices={state.plan.notices} />
          <PlannedAdCardGrid plan={state.plan} />
        </Column>
      );
    }
    default: {
      return state satisfies never;
    }
  }
};
